import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

export default function Footer() {
  const { t } = useTranslation();

  const quickLinks = [
    { to: '/', labelKey: 'nav_home' },
    { to: '/water-purification', labelKey: 'nav_water' },
    { to: '/windows-services', labelKey: 'nav_windows' },
    { to: '/about', labelKey: 'nav_about' },
    { to: '/contact', labelKey: 'nav_contact' },
  ];

  const serviceKeys = [
    'footer_svc1', 'footer_svc2', 'footer_svc3',
    'footer_svc4', 'footer_svc5', 'footer_svc6',
  ];

  return (
    <footer className="bg-[#1B3A6B]">
      {/* Main footer content */}
      <div className="max-w-7xl mx-auto px-6 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Brand column */}
          <div className="lg:col-span-1">
            <Link to="/">
              <img
                src="https://public.readdy.ai/ai/img_res/e0a102db-697a-45ac-b9ed-1cd4040cea79.png"
                alt="ORPM Investment LLC"
                className="h-12 w-auto mb-4"
              />
            </Link>
            <p className="text-slate-300 text-sm leading-relaxed mt-4">
              {t('footer_desc')}
            </p>
            <div className="flex items-center gap-4 mt-6">
              <a href="https://www.facebook.com/profile.php?id=61575383311735" rel="nofollow" target="_blank" className="w-9 h-9 rounded-full bg-white/10 hover:bg-sky-500 flex items-center justify-center transition-colors cursor-pointer" aria-label="Facebook">
                <i className="ri-facebook-fill text-white text-sm"></i>
              </a>
              <a href="https://www.instagram.com" rel="nofollow" target="_blank" className="w-9 h-9 rounded-full bg-white/10 hover:bg-sky-500 flex items-center justify-center transition-colors cursor-pointer" aria-label="Instagram">
                <i className="ri-instagram-line text-white text-sm"></i>
              </a>
              <a href="https://www.linkedin.com" rel="nofollow" target="_blank" className="w-9 h-9 rounded-full bg-white/10 hover:bg-sky-500 flex items-center justify-center transition-colors cursor-pointer" aria-label="LinkedIn">
                <i className="ri-linkedin-fill text-white text-sm"></i>
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-white font-bold text-sm uppercase tracking-widest mb-5">
              <a href="#quick-links" id="quick-links">{t('footer_quick_links')}</a>
            </h4>
            <ul className="space-y-3">
              {quickLinks.map((link) => (
                <li key={link.to}>
                  <Link
                    to={link.to}
                    className="text-slate-300 hover:text-sky-400 text-sm transition-colors flex items-center gap-2 cursor-pointer"
                  >
                    <div className="w-3 h-3 flex items-center justify-center">
                      <i className="ri-arrow-right-s-line text-sky-500 text-base"></i>
                    </div>
                    {t(link.labelKey)}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Services */}
          <div>
            <h4 className="text-white font-bold text-sm uppercase tracking-widest mb-5">
              <a href="#our-services" id="our-services">{t('footer_our_services')}</a>
            </h4>
            <ul className="space-y-3">
              {serviceKeys.map((key) => (
                <li key={key} className="text-slate-300 text-sm flex items-center gap-2">
                  <div className="w-3 h-3 flex items-center justify-center">
                    <i className="ri-checkbox-circle-line text-sky-500 text-sm"></i>
                  </div>
                  {t(key)}
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="text-white font-bold text-sm uppercase tracking-widest mb-5">
              <a href="#contact-info" id="contact-info">{t('footer_contact_us')}</a>
            </h4>
            <ul className="space-y-4">
              <li className="flex items-start gap-3 text-slate-300 text-sm">
                <div className="w-5 h-5 flex items-center justify-center mt-0.5 shrink-0">
                  <i className="ri-map-pin-line text-sky-400 text-base"></i>
                </div>
                <span style={{ whiteSpace: 'pre-line' }}>{t('footer_address')}</span>
              </li>
              <li>
                <a href="tel:+15615122820" className="flex items-center gap-3 text-slate-300 hover:text-sky-400 text-sm transition-colors cursor-pointer">
                  <div className="w-5 h-5 flex items-center justify-center shrink-0">
                    <i className="ri-phone-line text-sky-400 text-base"></i>
                  </div>
                  +1 (561) 512-2820
                </a>
              </li>
              <li>
                <a href="mailto:info@orpminvestment.com" className="flex items-center gap-3 text-slate-300 hover:text-sky-400 text-sm transition-colors cursor-pointer">
                  <div className="w-5 h-5 flex items-center justify-center shrink-0">
                    <i className="ri-mail-line text-sky-400 text-base"></i>
                  </div>
                  info@orpminvestment.com
                </a>
              </li>
              <li className="flex items-start gap-3 text-slate-300 text-sm">
                <div className="w-5 h-5 flex items-center justify-center mt-0.5 shrink-0">
                  <i className="ri-time-line text-sky-400 text-base"></i>
                </div>
                <span style={{ whiteSpace: 'pre-line' }}>{t('footer_hours')}</span>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Bottom bar */}
      <div className="border-t border-white/10 bg-[#122D56]">
        <div className="max-w-7xl mx-auto px-6 py-5 flex flex-col sm:flex-row items-center justify-between gap-3">
          <p className="text-slate-400 text-xs">
            {t('footer_copyright')}
          </p>
          <div className="flex items-center gap-6 flex-wrap justify-center">
            <span className="text-slate-400 text-xs">{t('footer_licensed')}</span>
            <span className="text-slate-500 text-xs">|</span>
            <a href="#privacy" className="text-slate-400 hover:text-sky-400 text-xs transition-colors cursor-pointer">{t('footer_privacy')}</a>
            <span className="text-slate-500 text-xs">|</span>
            <a href="#terms" className="text-slate-400 hover:text-sky-400 text-xs transition-colors cursor-pointer">{t('footer_terms')}</a>
          </div>
        </div>
      </div>
    </footer>
  );
}
